// Load the SDK only after a customer requests virtual try-on.
let sdkPromise;
function loadVtoSdk(localUrl) {
  if (window.FitMix) return Promise.resolve(window.FitMix);
  if (sdkPromise) return sdkPromise;
  const load = (src) => new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const timer = setTimeout(() => { script.remove(); reject(new Error('Try-on SDK timed out')); }, 15000);
    script.src = src;
    script.onload = () => {
      clearTimeout(timer);
      window.FitMix ? resolve(window.FitMix) : reject(new Error('Try-on SDK unavailable'));
    };
    script.onerror = () => { clearTimeout(timer); script.remove(); reject(new Error('Try-on SDK failed')); };
    document.head.append(script);
  });
  sdkPromise = load(localUrl)
    .catch(() => load('https://vto-advanced-integration-api.fittingbox.com/index.js'))
    .catch((error) => { sdkPromise = null; throw error; });
  return sdkPromise;
}

class VTOwidget extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.events = new AbortController();
    this.dialog = this.querySelector('dialog');
    this.status = this.querySelector('[data-vto-status]');
    this.sequence = 0;
    const options = { signal: this.events.signal };
    this.querySelector('.vto-close-button').addEventListener('click', () => this.stopVto(), options);
    this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.stopVto(); }, options);
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.stopVto();
    }, options);
  }

  disconnectedCallback() {
    this.stopVto();
    this.events?.abort();
    this.events = null;
    this.instance?.remove?.();
    this.instance = null;
    this.ready = null;
  }

  async createWidget() {
    if (this.ready) return this.ready;
    this.ready = (async () => {
      const sdk = await loadVtoSdk(this.dataset.sdkUrl);
      if (!this.isConnected) throw new Error('Try-on closed');
      await new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Try-on initialization timed out')), 20000);
        try {
          this.instance = sdk.createWidget('fitmix-container', {
            apiKey: '6zQDde5kfRSG1gc2efp3LJN-U4MwCAyLyM9Ch-o6KnDVPbgB7CNgLA',
            onStopVto: () => this.stopVto(false),
            onIssue: (issue) => this.handleIssue(issue),
          }, () => { clearTimeout(timer); resolve(); });
        } catch (error) { clearTimeout(timer); reject(error); }
      });
    })().catch((error) => {
      this.instance?.remove?.();
      this.instance = null;
      this.ready = null;
      throw error;
    });
    return this.ready;
  }

  handleIssue(issue) {
    if (!this.dialog?.open) return;
    // Fittingbox reports temporary tracking and latency conditions through
    // onIssue as well. They do not mean that the live session has failed.
    if (issue?.cameraAccessDenied) {
      this.status.textContent = 'Camera access is needed for virtual try-on. Please allow camera access and try again.';
    } else if (issue?.noCameraFound || issue?.liveIncompatibleBrowser || issue?.liveIncompatibleOS) {
      this.status.textContent = 'Live virtual try-on is unavailable on this device or browser.';
    } else if (issue?.detectionFailed || issue?.protocolFailed || issue?.frameNotFound || issue?.licenseNotFound || issue?.serverNotResponding) {
      this.status.textContent = 'Virtual try-on could not start. Please close and try again.';
    }
  }

  async openVto(barcode, trigger) {
    if (!barcode || this.dialog.open) return;
    const sequence = ++this.sequence;
    this.trigger = trigger;
    this.previousOverflow = document.body.style.overflow;
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    this.status.textContent = 'Loading virtual try-on…';
    this.dialog.setAttribute('aria-busy', 'true');
    try {
      await this.createWidget();
      if (!this.isConnected || sequence !== this.sequence || !this.dialog.open) return;
      this.instance.setFrame(barcode);
      this.instance.startVto('live');
      this.status.textContent = '';
    } catch {
      if (sequence === this.sequence && this.dialog.open) {
        this.status.textContent = 'Virtual try-on could not load. Please close and try again.';
      }
    } finally {
      if (sequence === this.sequence) this.dialog.removeAttribute('aria-busy');
    }
  }

  stopVto(stopCamera = true) {
    if (!this.dialog?.open) return;
    this.sequence++;
    this.dialog.close();
    document.body.style.overflow = this.previousOverflow;
    this.dialog.removeAttribute('aria-busy');
    if (stopCamera) this.instance?.stopVto();
    if (this.trigger?.isConnected) this.trigger.focus();
  }
}
if (!customElements.get('vto-widget')) customElements.define('vto-widget', VTOwidget);
